import React, { useState, useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Tag, Progress, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface StockTakingService extends TableService {
  export: (ids: unknown[], options: { locale: string }) => Promise<unknown>;
}

interface StockTakingRow {
  id: string;
  name?: string;
  note?: string | null;
  status?: string;
  is_blind?: boolean;
  mode?: string;
  total_shelves?: number;
  completed_shelves?: number;
  finalized_by_name?: string | null;
  finalized_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

interface StockTakingListViewProps {
  service: StockTakingService; // New
  role?: string;           // 角色標識：'operator' (Stock Taking 僅 Operator 使用)
  addJob?: unknown;
}

const StockTakingListView: React.FC<StockTakingListViewProps> = ({
  service, // New
  role = 'operator',           // 角色標識：'operator' (Stock Taking 僅 Operator 使用)
  addJob
}) => {
  const { t, i18n } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob as ((jobId: string, jobName: string, ...args: any[]) => void) | undefined)

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: 'models.stock_taking.name',
      persist: true,
      sortable: true,
      sortFieldName: 'stock_takings.name',
      width: '180px',
      wrap: true,
      cell: ({ id, name }: StockTakingRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/stock_takings/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/${role}/stock_takings/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: 'models.stock_taking.note',
      sortable: false,
      width: '200px',
      wrap: true,
      selector: ({ note }: StockTakingRow) => note || '-',
    },
    {
      name: (
        <Space size={4}>
          {t('models.stock_taking.status')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.stock_takings.list.column_tooltips.status')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      cell: ({ status }: StockTakingRow) => {
        const colorMap: Record<string, string> = {
          not_started: 'default',
          in_progress: 'blue',
          approved:    'green',
          rejected:    'red',
        }
        return (
          <Tag color={colorMap[status || ''] || 'default'}>
            {t(`enums.stock_taking.status.${status}`)}
          </Tag>
        )
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.stock_taking.is_blind')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.stock_takings.list.column_tooltips.is_blind')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      cell: ({ is_blind }: StockTakingRow) => (
        <Tag color={is_blind ? 'orange' : 'default'}>
          {t(`shared.stock_takings.list.blind_types.${is_blind ? 'blind' : 'normal'}`)}
        </Tag>
      ),
    },
    {
      name: (
        <Space size={4}>
          {t('models.stock_taking.mode')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.stock_takings.list.column_tooltips.mode')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      cell: ({ mode }: StockTakingRow) => (
        <Tag color={mode === 'static' ? 'purple' : 'cyan'}>
          {t(`enums.stock_taking.mode.${mode}`)}
        </Tag>
      ),
    },
    {
      name: (
        <Space size={4}>
          {t('models.stock_taking.progress')}
          <Tooltip title={t('shared.stock_takings.list.column_tooltips.progress')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '150px',
      cell: ({ total_shelves, completed_shelves }: StockTakingRow) => {
        const total = total_shelves || 0
        const completed = completed_shelves || 0
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Progress
              percent={percentage}
              size="small"
              style={{ flex: 1, minWidth: '60px' }}
            />
            <span style={{ fontSize: '12px', color: '#999' }}>
              {completed}/{total}
            </span>
          </div>
        )
      },
    },
    {
      nameKey: 'models.stock_taking.finalized_by',
      sortable: false,
      width: '120px',
      wrap: true,
      selector: ({ finalized_by_name }: StockTakingRow) => finalized_by_name || '-',
    },
    {
      nameKey: 'models.stock_taking.finalized_at',
      sortable: true,
      sortFieldName: 'stock_takings.finalized_at',
      width: '150px',
      wrap: true,
      selector: ({ finalized_at }: StockTakingRow) => finalized_at || '-',
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'stock_takings.created_at',
      width: '150px',
      wrap: true,
      selector: ({ created_at }: StockTakingRow) => created_at || '-',
    },
  ], [role, t])

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.stock_taking.name'),
      component: MultipleValue,
      inputName: 'stock_takings.name',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.name.title'),
        content: t('shared.stock_takings.list.filter_tooltips.name.content'),
      },
    },
    {
      label: t('models.stock_taking.status'),
      component: MultipleValue,
      inputName: 'status',
      createable: false,
      defaultOptions: [
        { label: t('enums.stock_taking.status.not_started'), value: 'not_started' },
        { label: t('enums.stock_taking.status.in_progress'), value: 'in_progress' },
        { label: t('enums.stock_taking.status.approved'),    value: 'approved' },
        { label: t('enums.stock_taking.status.rejected'),    value: 'rejected' },
      ],
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.status.title'),
        content: t('shared.stock_takings.list.filter_tooltips.status.content'),
      },
    },
    {
      label: t('models.stock_taking.is_blind'),
      component: MultipleValue,
      inputName: 'is_blind',
      createable: false,
      defaultOptions: [
        { label: t('enums.stock_taking.blind_type.normal'), value: false },
        { label: t('enums.stock_taking.blind_type.blind'), value: true },
      ],
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.is_blind.title'),
        content: t('shared.stock_takings.list.filter_tooltips.is_blind.content'),
      },
    },
    {
      label: t('models.stock_taking.mode'),
      component: MultipleValue,
      inputName: 'mode',
      createable: false,
      defaultOptions: [
        { label: t('enums.stock_taking.mode.dynamic'), value: 'dynamic' },
        { label: t('enums.stock_taking.mode.static'), value: 'static' },
      ],
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.mode.title'),
        content: t('shared.stock_takings.list.filter_tooltips.mode.content'),
      },
    },
    {
      label: t('models.stock_taking.finalized_at'),
      component: FullDayRangePicker,

      inputName: 'stock_takings.finalized_at',
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.finalized_at.title'),
        content: t('shared.stock_takings.list.filter_tooltips.finalized_at.content'),
      },
    },
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,

      inputName: 'stock_takings.created_at',
      tooltip: {
        title: t('shared.stock_takings.list.filter_tooltips.created_at.title'),
        content: t('shared.stock_takings.list.filter_tooltips.created_at.content'),
      },
    },
  ], [t])

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'common.button.export',
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected) => executeBatchOperation({
        task: () => service.export(Array.from(selected as Iterable<unknown>), { locale: i18n.language }),
        jobName: 'common.button.export'
      })
    } as TableAction
  ], [executeBatchOperation, service, i18n])

  return (
    <TableIndex
      columns={columns}
      supportRules={supportRules}
      service={service}
      searchable={{
        match: 'prefix',
        searchFields: [
          t('models.stock_taking.name'),
          t('models.stock_taking.note'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role}StockTakingsTable`}
      defaultQuery={{
        sorting_field: 'stock_takings.id',
        sorting_direction: 'desc',
      }}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      filterHelpExample={t('shared.stock_takings.list.filter_help_example') as string}
      filterDocsUrl="/docs/operator/stock_takings/list#features"
    />
  )
}

export default StockTakingListView
